import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Field, Initials, Notice, Spinner } from '../../components/ui';
import { api, ApiError } from '../../lib/api';
import { config } from '../../config';
import { relativeDate } from '../../lib/school';
import type { Invite, Person, Seats } from '../../lib/people';
import { getLibrary, grantAccess, revokeAccess } from '../../lib/frameworks';
import { SearchAdd } from '../../components/SearchAdd';
import { Dialog } from '../../components/Dialog';
import '../Page.css';
import '../../components/Form.css';
import './Admin.css';
import { BackLink } from '../../components/BackLink';
import { ManagePerson, type AdminPerson } from './ManagePerson';

interface Detail {
  school: {
    id: string;
    name: string;
    type?: 'organisation' | 'personal';
    invite_code: string | null;
    phase: string | null;
    phase_other?: string | null;
    country: string | null;
    trust_name: string | null;
    seat_limit: number | null;
    created_at: string;
    onboarded_at: string | null;
    terms_accepted_at: string | null;
    setup?: 'done' | 'no_owner' | 'in_progress';
    setup_missing?: string[];
    paused_at?: string | null;
    /** Absent on older APIs. */
    exclude_from_public_stats?: number;
    /** Absent on older APIs. */
    public_case_study_stats?: number;
  };
  usage?: Usage;
  options?: { educationSettings: { value: string; label: string }[]; quickCountries: string[]; allCountries: string[] };
  seats: Seats;
  members: Person[];
  invites: Invite[];
  /** Absent on older APIs. */
  frameworks?: { id: string; name: string; isDefault: boolean; own: boolean }[];
}

const ROLE: Record<string, string> = { teacher: 'Teacher', school_leader: 'Leader', system_admin: 'Admin' };

interface Usage {
  lessonsByMonth: { month: string; lessons: number }[];
  lessons: number;
  activeLast30Days: number;
  costUsd: { scoringLast30Days: number; scoringAllTime: number; frameworkBuilds: number };
}

export function AdminSchoolPage() {
  const { id } = useParams();
  const qc = useQueryClient();
  const detail = useQuery({ queryKey: ['admin-school', id], queryFn: () => api<Detail>(`/admin/schools/${id}`) });
  const [limit, setLimit] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);
  const library = useQuery({ queryKey: ['admin-frameworks'], queryFn: getLibrary });
  const [removing, setRemoving] = useState<{ id: string; name: string } | null>(null);
  // Arriving from search with ?person= highlights them and opens Manage.
  const [params, setParams] = useSearchParams();
  const [managing, setManaging] = useState<AdminPerson | null>(null);
  const highlight = params.get('person');
  useEffect(() => {
    const m = highlight ? detail.data?.members.find((x) => x.id === highlight) : null;
    if (m && detail.data) setManaging({ id: m.id, name: m.name, email: m.email, role: m.role, isOwner: m.isOwner, schoolId: detail.data.school.id, schoolName: detail.data.school.name });
  }, [highlight, detail.data]);
  const [inviteBusy, setInviteBusy] = useState<string | null>(null);
  const inviteAction = async (inviteId: string, email: string, kind: 'resend' | 'cancel') => {
    setInviteBusy(inviteId);
    setMessage(null);
    try {
      if (kind === 'resend') await api(`/admin/schools/${id}/invites/${inviteId}/resend`, { method: 'POST', body: '{}' });
      else await api(`/admin/schools/${id}/invites/${inviteId}`, { method: 'DELETE' });
      await qc.invalidateQueries({ queryKey: ['admin-school', id] });
      setMessage({ tone: 'success', text: kind === 'resend' ? `Invite sent to ${email} again.` : `Invite to ${email} cancelled. Its seat is free.` });
    } catch (err) {
      setMessage({ tone: 'error', text: err instanceof ApiError ? err.message : 'That didn’t work. Try again.' });
    } finally {
      setInviteBusy(null);
    }
  };

  const fwAction = async <T,>(fn: () => Promise<T>, success: string | ((result: T) => string)) => {
    setMessage(null);
    try {
      const result = await fn();
      await Promise.all([qc.invalidateQueries({ queryKey: ['admin-school', id] }), qc.invalidateQueries({ queryKey: ['admin-frameworks'] })]);
      setMessage({ tone: 'success', text: typeof success === 'function' ? success(result) : success });
    } catch (err) {
      setMessage({ tone: 'error', text: err instanceof ApiError ? err.message : 'That didn’t work. Try again.' });
    }
  };

  useEffect(() => {
    if (detail.data) setLimit(detail.data.school.seat_limit == null ? '' : String(detail.data.school.seat_limit));
  }, [detail.data]);

  const saveSeats = async () => {
    setBusy(true);
    setMessage(null);
    try {
      await api(`/admin/schools/${id}/seats`, { method: 'PUT', body: JSON.stringify({ seatLimit: limit.trim() === '' ? null : Number(limit) }) });
      await qc.invalidateQueries({ queryKey: ['admin-school', id] });
      setMessage({ tone: 'success', text: limit.trim() ? `Seat limit set to ${limit}.` : 'Seat limit removed.' });
    } catch (err) {
      setMessage({ tone: 'error', text: err instanceof ApiError ? err.message : 'That didn’t save.' });
    } finally {
      setBusy(false);
    }
  };

  if (detail.isLoading) return <Spinner />;
  if (detail.isError || !detail.data) return <Notice tone="error">Couldn&rsquo;t load this school.</Notice>;
  const { school, seats, members, invites } = detail.data;
  const frameworks = detail.data.frameworks ?? [];
  // One person's own account: no seats, team or trust to manage.
  const personal = school.type === 'personal';
  const has = new Set(frameworks.map((f) => f.id));
  const addable = (library.data ?? []).filter((f) => f.status === 'approved' && !has.has(f.id) && (!f.school_id || f.school_id === school.id));

  return (
    <div className="page">
      <BackLink to={personal ? '/admin/schools?view=personal' : '/admin/schools'}>{personal ? 'Personal accounts' : 'Organisations'}</BackLink>
      <header className="page-head">
        <p className="eyebrow">
          {personal ? 'Personal account' : [school.trust_name, school.invite_code].filter(Boolean).join(' · ') || 'Organisation'}
        </p>
        <h1>
          {school.name}
          {school.paused_at ? <span className="chip paused-chip">Paused</span> : null}
        </h1>
        <p className="muted">
          Joined {relativeDate(school.created_at).toLowerCase()} ·{' '}
          {school.setup === 'done'
            ? `set up ${school.onboarded_at ? relativeDate(school.onboarded_at).toLowerCase() : ''}`
            : school.setup === 'no_owner'
              ? 'setup not finished, and nobody who can finish it yet'
              : school.setup_missing?.length
                ? `setup not finished: needs ${school.setup_missing.join(', ')}`
                : 'setup not finished: the owner still has to complete it'}
        </p>
      </header>

      {message ? <Notice tone={message.tone}>{message.text}</Notice> : null}

      <ManagePerson
        person={managing}
        onClose={() => {
          setManaging(null);
          if (highlight) setParams({}, { replace: true });
        }}
        onDone={(text) => {
          setManaging(null);
          if (highlight) setParams({}, { replace: true });
          setMessage({ tone: 'success', text });
          qc.invalidateQueries({ queryKey: ['admin-school', id] });
        }}
      />

      {detail.data.options ? (
        <DetailsSection
          school={school}
          options={detail.data.options}
          onSaved={(text) => {
            setMessage({ tone: 'success', text });
            qc.invalidateQueries({ queryKey: ['admin-school', id] });
            qc.invalidateQueries({ queryKey: ['admin-schools'] });
          }}
          onError={(text) => setMessage({ tone: 'error', text })}
        />
      ) : null}

      {/* A personal account's person is shown below; only an invite not yet taken up needs the owner panel. */}
      {!personal || !members.length ? (
        <OwnerSection
          schoolId={school.id}
          personal={personal}
          members={members}
          invites={invites}
          onChanged={(text) => setMessage({ tone: 'success', text })}
          onError={(text) => setMessage({ tone: 'error', text })}
        />
      ) : null}

      {personal ? null : (
      <section className="panel form-section">
        <h2>Seats</h2>
        <p className="muted">
          {seats.used} in use ({seats.members} people, {seats.pendingInvites} invited). Leave empty for no limit.
        </p>
        <div className="inline-form">
          <Field id="admin-seat-limit" label="Seat limit" inputMode="numeric" value={limit} onChange={(e) => setLimit(e.target.value.replace(/[^0-9]/g, ''))} />
          <Button onClick={saveSeats} loading={busy}>
            Save
          </Button>
        </div>
      </section>
      )}

      <section className="panel form-section">
        <div>
          <h2>Frameworks</h2>
          <p className="muted">What {school.name} can use. Their default is what new lessons are scored against.</p>
        </div>
        {frameworks.length ? (
          <div className="list">
            {frameworks.map((f) => (
              <div key={f.id} className="list-row fw-org-row">
                <div className="list-main">
                  <p className="list-title">
                    <Link to={`/admin/frameworks/${f.id}`}>{f.name}</Link>
                  </p>
                  {f.own ? <p className="list-sub">Made for them</p> : null}
                </div>
                <div className="fw-org-actions">
                  {f.isDefault ? (
                    <span className="chip chip-accent">Their default</span>
                  ) : (
                    <Button variant="ghost" onClick={() => fwAction(() => grantAccess(f.id, school.id, true), `${f.name} is now their default.`)}>
                      Make default
                    </Button>
                  )}
                  {f.own ? null : (
                    <Button
                      variant="ghost"
                      onClick={() =>
                        f.isDefault ? setRemoving({ id: f.id, name: f.name }) : fwAction(() => revokeAccess(f.id, school.id), `${f.name} removed.`)
                      }
                    >
                      Remove
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="faint">None yet, so their lessons use the standard EducAitor framework.</p>
        )}
        <SearchAdd
          label="Add a framework"
          placeholder="Search published frameworks"
          noun="framework"
          items={addable.map((f) => ({ id: f.id, title: f.name, sub: f.description || null }))}
          onAdd={(f) =>
            fwAction(
              () => grantAccess(f.id, school.id, 'ifNone'),
              (r) => (r.setAsDefault ? `${f.title} added. It’s their default.` : `${f.title} added. Their default hasn’t changed.`),
            )
          }
        />
      </section>

      <section className="panel">
        <h2>{personal ? 'Their account' : 'People'}</h2>
        <div className="list">
          {members.map((m) => (
            <div key={m.id} className="list-row admin-person-row" data-highlight={m.id === highlight || undefined}>
              <Initials name={m.name} />
              <div className="list-main">
                <p className="list-title">{m.name}</p>
                <p className="list-sub">
                  {m.email} · {m.lessons === 0 ? 'no lessons yet' : `${m.lessons} lesson${m.lessons === 1 ? '' : 's'}, ${relativeDate(m.lastActiveAt).toLowerCase()}`}
                </p>
              </div>
              <span className="row-chips">
                <span className={`chip${m.role !== 'teacher' ? ' chip-accent' : ''}`}>{ROLE[m.role] ?? m.role}</span>
                {m.isOwner ? <span className="chip chip-accent">Owner</span> : null}
              </span>
              <Button
                variant="ghost"
                onClick={() =>
                  setManaging({ id: m.id, name: m.name, email: m.email, role: m.role, isOwner: m.isOwner, schoolId: school.id, schoolName: school.name })
                }
              >
                Manage
              </Button>
            </div>
          ))}
        </div>
        {invites.filter((i) => !i.makeOwner).length ? (
          <>
            <h3 className="invites-title">Invited</h3>
            <div className="list">
              {invites
                .filter((i) => !i.makeOwner)
                .map((i) => (
                  <div key={i.id} className="list-row admin-invite-row">
                    <div className="list-main">
                      <p className="list-title">{i.email}</p>
                      <p className="list-sub">
                        {i.role === 'school_leader' ? 'Leader' : 'Teacher'} ·{' '}
                        {i.expired ? 'invite expired' : `invited ${relativeDate(i.createdAt).toLowerCase()}${i.invitedBy ? ` by ${i.invitedBy}` : ''}`}
                      </p>
                    </div>
                    <Button variant="ghost" onClick={() => inviteAction(i.id, i.email, 'resend')} loading={inviteBusy === i.id}>
                      {i.expired ? 'Send again' : 'Resend'}
                    </Button>
                    <Button variant="ghost" onClick={() => inviteAction(i.id, i.email, 'cancel')} disabled={inviteBusy === i.id}>
                      Cancel
                    </Button>
                  </div>
                ))}
            </div>
          </>
        ) : null}
      </section>

      {detail.data.usage ? <UsageSection usage={detail.data.usage} /> : null}

      <AccountSection
        school={school}
        people={members.filter((m) => m.role !== 'system_admin').length}
        onChanged={(text) => {
          setMessage({ tone: 'success', text });
          qc.invalidateQueries({ queryKey: ['admin-school', id] });
          qc.invalidateQueries({ queryKey: ['admin-schools'] });
        }}
        onError={(text) => setMessage({ tone: 'error', text })}
      />

      <Dialog open={!!removing} onClose={() => setRemoving(null)} title={`Remove ${removing?.name ?? ''}?`}>
        <p className="muted">It&rsquo;s their default. Their new lessons will be scored against the standard EducAitor framework until you choose another.</p>
        <div className="dialog-actions">
          <Button variant="ghost" onClick={() => setRemoving(null)}>
            Keep it
          </Button>
          <Button
            variant="danger"
            onClick={() => {
              const r = removing;
              setRemoving(null);
              if (r) fwAction(() => revokeAccess(r.id, school.id), `${r.name} removed.`);
            }}
          >
            Remove
          </Button>
        </div>
      </Dialog>

    </div>
  );
}

/**
 * Who owns the account. An organisation EducAitor set up starts with an owner
 * invite; this shows whether it's been taken up, and resends or replaces it.
 */
function OwnerSection({
  schoolId,
  personal,
  members,
  invites,
  onChanged,
  onError,
}: {
  schoolId: string;
  /** A personal account: the invite is for the person who'll use it. */
  personal?: boolean;
  members: Person[];
  invites: Invite[];
  onChanged: (text: string) => void;
  onError: (text: string) => void;
}) {
  const qc = useQueryClient();
  const owners = members.filter((m) => m.isOwner);
  const ownerInvites = invites.filter((i) => i.makeOwner);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState<string | null>(null);

  const send = async (to: string, who: string | null, key: string) => {
    setBusy(key);
    try {
      await api(`/admin/schools/${schoolId}/owner-invite`, { method: 'POST', body: JSON.stringify({ email: to, name: who }) });
      await qc.invalidateQueries({ queryKey: ['admin-school', schoolId] });
      setEmail('');
      setName('');
      onChanged(`Owner invite sent to ${to}.`);
    } catch (err) {
      onError(err instanceof ApiError ? err.message : 'That didn’t send. Try again.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="panel form-section">
      <div>
        <h2>{personal ? 'Their invite' : 'Owner'}</h2>
        <p className="muted">
          {personal
            ? 'They create their account from this email, then do a short setup on the web.'
            : 'The account owner finishes setup, manages seats and invites leaders.'}
        </p>
      </div>
      {owners.length ? (
        <div className="list">
          {owners.map((o) => (
            <div key={o.id} className="list-row fw-org-row">
              <div className="list-main">
                <p className="list-title">{o.name}</p>
                <p className="list-sub">{o.email}</p>
              </div>
              <span className="chip chip-accent">Owner</span>
            </div>
          ))}
        </div>
      ) : null}
      {ownerInvites.length ? (
        <div className="list">
          {ownerInvites.map((i) => (
            <div key={i.id} className="list-row fw-org-row">
              <div className="list-main">
                <p className="list-title">{i.email}</p>
                <p className="list-sub">
                  {i.expired ? 'Invite expired' : `Invited ${relativeDate(i.createdAt).toLowerCase()}, not accepted yet`}
                </p>
              </div>
              <Button variant="ghost" onClick={() => send(i.email, null, i.id)} loading={busy === i.id}>
                {i.expired ? 'Send a new invite' : 'Resend'}
              </Button>
            </div>
          ))}
        </div>
      ) : null}
      {!owners.length && !ownerInvites.some((i) => !i.expired) ? (
        <div className="inline-form">
          <Field id="owner-invite-name" label="Owner’s name" value={name} onChange={(e) => setName(e.target.value)} />
          <Field id="owner-invite-email" label="Owner’s email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          <Button onClick={() => send(email.trim(), name.trim() || null, 'new')} loading={busy === 'new'} disabled={!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())}>
            Invite an owner
          </Button>
        </div>
      ) : null}
    </section>
  );
}

/**
 * Correct an organisation's details: its name, whether it's an organisation
 * or one person's account, its trust, setting and country. The owner keeps
 * the rest (staff size, priorities) up to date in Organisation settings.
 */
function DetailsSection({
  school,
  options,
  onSaved,
  onError,
}: {
  school: Detail['school'];
  options: NonNullable<Detail['options']>;
  onSaved: (text: string) => void;
  onError: (text: string) => void;
}) {
  const [name, setName] = useState(school.name);
  const [type, setType] = useState<'organisation' | 'personal'>(school.type ?? 'organisation');
  const [trust, setTrust] = useState(school.trust_name ?? '');
  const [phase, setPhase] = useState(school.phase ?? '');
  const [phaseOther, setPhaseOther] = useState(school.phase_other ?? '');
  const [country, setCountry] = useState(school.country ?? '');
  const [excluded, setExcluded] = useState(Boolean(school.exclude_from_public_stats));
  const [caseStudy, setCaseStudy] = useState(Boolean(school.public_case_study_stats));
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setName(school.name);
    setType(school.type ?? 'organisation');
    setTrust(school.trust_name ?? '');
    setPhase(school.phase ?? '');
    setPhaseOther(school.phase_other ?? '');
    setCountry(school.country ?? '');
    setExcluded(Boolean(school.exclude_from_public_stats));
    setCaseStudy(Boolean(school.public_case_study_stats));
  }, [school]);

  const changed =
    name.trim() !== school.name ||
    type !== (school.type ?? 'organisation') ||
    trust.trim() !== (school.trust_name ?? '') ||
    phase !== (school.phase ?? '') ||
    phaseOther.trim() !== (school.phase_other ?? '') ||
    country !== (school.country ?? '') ||
    excluded !== Boolean(school.exclude_from_public_stats) ||
    caseStudy !== Boolean(school.public_case_study_stats);

  const save = async () => {
    setBusy(true);
    try {
      await api(`/admin/schools/${school.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ name, type, trustName: trust, phase: phase || null, phaseOther: phase === 'other' ? phaseOther : null, country: country || null, excludeFromPublicStats: excluded, publicCaseStudyStats: caseStudy }),
      });
      onSaved('Details saved.');
    } catch (err) {
      onError(err instanceof ApiError ? err.message : 'That didn’t save. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const countries = [...options.quickCountries, ...options.allCountries.filter((c) => !options.quickCountries.includes(c))];
  return (
    <section className="panel form-section">
      <div>
        <h2>Details</h2>
        <p className="muted">
          {type === 'personal' ? 'Their name and country.' : 'The owner keeps staff size and priority areas up to date in Organisation settings.'}
        </p>
      </div>
      <div className="segmented" role="group" aria-label="Type of account">
        <button type="button" aria-pressed={type === 'organisation'} onClick={() => setType('organisation')}>
          Organisation
        </button>
        <button type="button" aria-pressed={type === 'personal'} onClick={() => setType('personal')}>
          Personal
        </button>
      </div>
      {type === 'personal' ? <p className="muted">One person using EducAitor on their own. Their setup only needs their own profile.</p> : null}
      <div className="details-grid">
        <Field id="org-name" label="Name" value={name} onChange={(e) => setName(e.target.value)} />
        {type === 'personal' ? null : (
          <Field id="org-trust" label="Trust or group (optional)" value={trust} onChange={(e) => setTrust(e.target.value)} />
        )}
        {type === 'personal' ? null : (
        <div className="field">
          <label htmlFor="org-phase">Education setting</label>
          <select id="org-phase" className="select" value={phase} onChange={(e) => setPhase(e.target.value)}>
            <option value="">Not given</option>
            {options.educationSettings.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        )}
        {type !== 'personal' && phase === 'other' ? (
          <Field id="org-phase-other" label="Their setting" value={phaseOther} onChange={(e) => setPhaseOther(e.target.value)} />
        ) : null}
        <div className="field">
          <label htmlFor="org-country">Country</label>
          <select id="org-country" className="select" value={country} onChange={(e) => setCountry(e.target.value)}>
            <option value="">Not given</option>
            {countries.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
      </div>
      <label className="auth-check">
        <input type="checkbox" checked={excluded} onChange={(e) => setExcluded(e.target.checked)} />
        <span>Leave out of public figures: a test, demo or internal account, not counted in the numbers on educaitor.ai</span>
      </label>
      {type === 'personal' ? null : (
        <label className="auth-check">
          <input type="checkbox" checked={caseStudy} onChange={(e) => setCaseStudy(e.target.checked)} />
          <span>Public case study: show this organisation&rsquo;s own totals on educaitor.ai. Only with their written agreement; nothing shows until 5 or more educators have recorded.</span>
        </label>
      )}
      {type !== 'personal' && school.public_case_study_stats ? (
        <p className="muted">
          Figures for the website: <code>{`${config.apiUrl}/public/stats/organisations/${school.id}`}</code>
        </p>
      ) : null}
      <div className="form-actions">
        <Button onClick={save} loading={busy} disabled={!changed || !name.trim()}>
          Save details
        </Button>
      </div>
    </section>
  );
}

const money = (usd: number) => (usd < 0.01 && usd > 0 ? '<$0.01' : `$${usd.toFixed(2)}`);
const monthLabel = (ym: string) => new Date(`${ym}-01T12:00:00`).toLocaleDateString('en-GB', { month: 'short' });

/** Lessons per month, who's active and what the AI costs: for renewal and pricing conversations. */
function UsageSection({ usage }: { usage: Usage }) {
  const top = Math.max(1, ...usage.lessonsByMonth.map((m) => m.lessons));
  return (
    <section className="panel form-section">
      <div>
        <h2>Usage</h2>
        <p className="muted">Transcription and the coach aren&rsquo;t costed per lesson yet, so AI cost here is scoring and framework builds.</p>
      </div>
      <div className="usage-stats">
        <div className="stat">
          <span className="stat-value">{usage.lessons}</span>
          <span className="stat-label">Lessons scored</span>
        </div>
        <div className="stat">
          <span className="stat-value">{usage.activeLast30Days}</span>
          <span className="stat-label">Active in the last 30 days</span>
        </div>
        <div className="stat">
          <span className="stat-value">{money(usage.costUsd.scoringLast30Days)}</span>
          <span className="stat-label">Scoring, last 30 days</span>
        </div>
        <div className="stat">
          <span className="stat-value">{money(usage.costUsd.scoringAllTime + usage.costUsd.frameworkBuilds)}</span>
          <span className="stat-label">AI cost, all time</span>
        </div>
      </div>
      <div className="usage-bars" role="img" aria-label={`Lessons per month: ${usage.lessonsByMonth.map((m) => `${monthLabel(m.month)} ${m.lessons}`).join(', ')}`}>
        {usage.lessonsByMonth.map((m) => (
          <div key={m.month} className="usage-bar">
            <span className="usage-bar-n">{m.lessons}</span>
            <span className="usage-bar-fill" style={{ height: `${Math.max(2, (m.lessons / top) * 72)}px` }} />
            <span className="usage-bar-label">{monthLabel(m.month)}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

/**
 * Pausing stops everyone in the organisation using EducAitor until it's
 * resumed; nothing is deleted. Deleting removes it and everyone in it for good,
 * so it asks for the organisation's name first.
 */
function AccountSection({
  school,
  people,
  onChanged,
  onError,
}: {
  school: Detail['school'];
  people: number;
  onChanged: (text: string) => void;
  onError: (text: string) => void;
}) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState<'pause' | 'delete' | null>(null);
  const [typed, setTyped] = useState('');
  const paused = !!school.paused_at;

  const togglePause = async () => {
    setBusy(true);
    try {
      await api(`/admin/schools/${school.id}/pause`, { method: 'POST', body: JSON.stringify({ paused: !paused }) });
      setConfirming(null);
      onChanged(paused ? `${school.name} is active again.` : `${school.name} is paused. Its people can’t use EducAitor until you resume it.`);
    } catch (err) {
      onError(err instanceof ApiError ? err.message : 'That didn’t work. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      const r = await api<{ deleted: number; signInsLeft: string[] }>(`/admin/schools/${school.id}`, {
        method: 'DELETE',
        body: JSON.stringify({ confirmName: typed }),
      });
      navigate('/admin/schools', {
        replace: true,
        state: {
          message: `${school.name} deleted, with ${r.deleted} ${r.deleted === 1 ? 'person' : 'people'}.${
            r.signInsLeft.length ? ` Their sign-in accounts need removing in AWS: ${r.signInsLeft.join(', ')}.` : ''
          }`,
        },
      });
    } catch (err) {
      onError(err instanceof ApiError ? err.message : 'That didn’t work. Try again.');
      setBusy(false);
    }
  };

  return (
    <section className="panel form-section">
      <div>
        <h2>Account</h2>
        <p className="muted">{paused ? `Paused ${relativeDate(school.paused_at!).toLowerCase()}. Nobody at ${school.name} can use EducAitor until you resume it.` : 'Active.'}</p>
      </div>
      <div className="form-actions">
        <Button variant="secondary" onClick={() => (paused ? togglePause() : setConfirming('pause'))} loading={busy && confirming !== 'delete'}>
          {paused ? 'Resume' : 'Pause organisation'}
        </Button>
        <Button
          variant="danger"
          onClick={() => {
            setTyped('');
            setConfirming('delete');
          }}
        >
          Delete organisation
        </Button>
      </div>

      <Dialog open={confirming === 'pause'} onClose={() => !busy && setConfirming(null)} title={`Pause ${school.name}?`}>
        <p className="muted">
          Its {people} {people === 1 ? 'person' : 'people'} won&rsquo;t be able to use EducAitor until you resume it. Their lessons, reports and settings are
          kept.
        </p>
        <div className="dialog-actions">
          <Button variant="ghost" onClick={() => setConfirming(null)} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={togglePause} loading={busy}>
            Pause
          </Button>
        </div>
      </Dialog>

      <Dialog open={confirming === 'delete'} onClose={() => !busy && setConfirming(null)} title={`Delete ${school.name}?`}>
        <p className="muted">
          This deletes the organisation and its {people} {people === 1 ? 'person' : 'people'}, with every lesson, report and framework request, for good. EducAitor
          admins in it are just taken out. It can&rsquo;t be undone.
        </p>
        <Field id="delete-org-confirm" label={`Type ${school.name} to confirm`} value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" />
        <div className="dialog-actions">
          <Button variant="ghost" onClick={() => setConfirming(null)} disabled={busy}>
            Cancel
          </Button>
          <Button variant="danger" onClick={remove} loading={busy} disabled={typed.trim() !== school.name}>
            Delete for good
          </Button>
        </div>
      </Dialog>
    </section>
  );
}
